import { StreamLanguage, StringStream, foldService } from '@codemirror/language';
import { tags } from '@lezer/highlight';

interface JsonataState {
    inBlockComment: boolean;
    /** Indentation (in columns) of the line where each still-open bracket was opened */
    openBrackets: number[];
    /** Whether a `/` here starts a regex literal rather than division, i.e. no value precedes it */
    regexAllowed: boolean;
}

/** Consume a `/pattern/flags` regex literal; `/` inside a `[...]` class or after `\` does not end it */
function readRegex (stream: StringStream): void {
    stream.next();
    let inClass = false;
    while (!stream.eol()) {
        const c = stream.next()!;
        if (c === '\\' && !stream.eol()) {
            stream.next();
        } else if (c === '[') {
            inClass = true;
        } else if (c === ']') {
            inClass = false;
        } else if (c === '/' && !inClass) {
            stream.eatWhile(/[im]/);
            return;
        }
    }
}

/** Read one token; `token` below wraps this to track whether a regex may follow */
function readToken (stream: StringStream, state: JsonataState): string | null {
    // Block comment continuation from previous line
    if (state.inBlockComment) {
        const idx = stream.string.indexOf('*/', stream.pos);
        if (idx !== -1) {
            stream.pos = idx + 2;
            state.inBlockComment = false;
        } else {
            stream.skipToEnd();
        }
        return 'comment';
    }

    if (stream.eatSpace()) { return null; }

    if (stream.match('/*')) {
        const idx = stream.string.indexOf('*/', stream.pos);
        if (idx !== -1) {
            stream.pos = idx + 2;
        } else {
            state.inBlockComment = true;
            stream.skipToEnd();
        }
        return 'comment';
    }

    if (state.regexAllowed && stream.peek() === '/') {
        readRegex(stream);
        return 'regexp';
    }

    if (stream.peek() === '"') {
        stream.next();
        while (!stream.eol()) {
            const c = stream.next()!;
            if (c === '\\' && !stream.eol()) { stream.next(); } else if (c === '"') { return 'string'; }
        }
        return 'string';
    }

    if (stream.peek() === '\'') {
        stream.next();
        while (!stream.eol()) {
            const c = stream.next()!;
            if (c === '\\' && !stream.eol()) { stream.next(); } else if (c === '\'') { return 'string'; }
        }
        return 'string';
    }

    if (stream.peek() === '`') {
        stream.next();
        while (!stream.eol() && stream.next() !== '`') { /* consume */ }
        return 'variable';
    }

    // $-prefixed variable or function ($$ is the root-context variable)
    if (stream.peek() === '$') {
        stream.next();
        if (stream.peek() === '$') {
            stream.next();
            return 'jsonata-variable';
        }
        stream.eatWhile(/[a-zA-Z0-9_]/);
        return 'jsonata-variable';
    }

    if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) {
        const word = stream.current();
        if (word === 'true' || word === 'false' || word === 'null' ||
            word === 'and' || word === 'or' || word === 'in') {
            return 'keyword';
        }
        return 'variable';
    }

    if (stream.match(/^[0-9]+(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/)) {
        return 'number';
    }

    if (stream.match(':=') || stream.match('!=') || stream.match('<=') ||
        stream.match('>=') || stream.match('~>') || stream.match('**') ||
        stream.match('..')) {
        return 'operator';
    }

    const ch = stream.next()!;
    if ('.,@#;:?|=<>&!~+-*/%^'.includes(ch)) {
        return 'operator';
    }
    if ('([{'.includes(ch)) {
        state.openBrackets.push(stream.indentation());
        return 'bracket';
    }
    if (')]}'.includes(ch)) {
        state.openBrackets.pop();
        return 'bracket';
    }

    return null;
}

export const jsonataStreamLanguage = StreamLanguage.define<JsonataState>({
    name: 'jsonata',

    tokenTable: {
        'jsonata-variable': tags.special(tags.variableName)
    },

    startState (): JsonataState {
        return { inBlockComment: false, openBrackets: [], regexAllowed: true };
    },

    token (stream, state): string | null {
        const style = readToken(stream, state);
        if (style && style !== 'comment') {
            // After a value (operand or closing bracket) a `/` is division; after anything else it starts a regex
            const valueEnd = style === 'bracket' ? ')]}'.includes(stream.current()) : style !== 'operator' && !/^(and|or|in)$/.test(stream.current());
            state.regexAllowed = !valueEnd;
        }
        return style;
    },

    // Indent one unit past the line that opened the innermost bracket; a closing bracket aligns with that line.
    // Returning null (top level / inside a block comment) falls back to keeping the current line's indentation.
    indent (state, textAfter, context): number | null {
        const depth = state.openBrackets.length;
        if (state.inBlockComment || depth === 0) { return null; }
        const base = state.openBrackets[depth - 1]!;
        return /^[)\]}]/.test(textAfter) ? base : base + context.unit;
    },

    languageData: {
        indentOnInput: /^\s*[)\]}]$/,
        closeBrackets: { brackets: ['(', '[', '{', '"', '\'', '`'] },
        commentTokens: { block: { open: '/*', close: '*/' } }
    }
});

/**
 * Bracket-based code folding for JSONata.
 * Folds lines ending with an opening bracket (`{`, `[`, or `(`) through to the corresponding matching closing bracket
 */
export const jsonataFoldService = foldService.of((state, lineStart, lineEnd) => {
    const lineText = state.doc.sliceString(lineStart, lineEnd);
    const match = /([{[(])\s*$/.exec(lineText);
    if (!match) { return null; }

    const openChar = match[1];
    const closeChar = openChar === '{' ? '}' : openChar === '[' ? ']' : ')';
    const openPos = lineStart + match.index;

    let depth = 1;
    let inString: string | null = null;
    let inBlockComment = false;
    let pos = openPos + 1;
    const docLen = state.doc.length;

    while (pos < docLen) {
        const line = state.doc.lineAt(pos);
        const text = state.doc.sliceString(pos, line.to);

        for (let i = 0; i < text.length; i++) {
            const c = text[i];
            const n = i + 1 < text.length ? text[i + 1] : '';

            if (inBlockComment) {
                if (c === '*' && n === '/') {
                    inBlockComment = false;
                    i++;
                }
                continue;
            }

            if (inString) {
                // Backticks escape identifiers and do not support backslash escapes.
                if (c === '\\' && inString !== '`') { i++; continue; }
                if (c === inString) { inString = null; }
                continue;
            }

            if (c === '/' && n === '*') {
                inBlockComment = true;
                i++;
                continue;
            }

            if (c === '"' || c === '\'' || c === '`') {
                inString = c;
                continue;
            }

            if (c === openChar) {
                depth++;
            } else if (c === closeChar) {
                depth--;
                if (depth === 0) {
                    return { from: openPos + 1, to: pos + i };
                }
            }
        }

        pos = line.to + 1;
    }

    return null;
});
